import { Box, CardContent, CardMedia, Skeleton, Stack, Typography } from '@mui/material'
import { path } from 'ramda'
import { useTranslation } from 'react-i18next'
import { CommittedProgramDisplay } from './CommittedProgramDisplay'

import { FriendlyError, Scrim } from '@cibo/ui'

import { FDRStatus } from '@cibo/core'
import { ProgramIcon, ProgramLimitMeter } from '@cibo/programs'
import { ProgramOpportunityBanner } from './ProgramOpportunityBanner'
import { ProgramOpportunityDisplayProps } from './ProgramOpportunityTypes'

// eslint-disable-next-line complexity
export const ProgramOpportunityDisplay = ({
  enrollmentCount,
  hideBanner,
  program,
  hideCurrency,
  isLoading,
  isError,
  opportunities,
  orgContent,
  stat,
  tokens,
}: ProgramOpportunityDisplayProps) => {
  const { t } = useTranslation('impact/pages/ProgramOpportunity')
  /**
   * @todo currency
   * support more than one currency
   */
  const programCurrency = path(['opportunities', 0, 'reward', 0, 'token'], program)

  const availableOpportunities = opportunities?.filter(
    ({ status, fields }) => status === FDRStatus.eligibleForEnrollment && !!fields
  )

  return (
    <>
      {isError ? (
        <FriendlyError message={t('couldNotLoadProgram')} />
      ) : (
        <>
          <CardMedia
            image={
              program?.content.fields.promoImages &&
              !program?.content.fields.promoImages?.[0].fields.file.url.includes('downloads')
                ? `${program?.content.fields.promoImages?.[0].fields.file.url}?h=500&fm=jpg&q=50`
                : undefined
            }
            sx={{ minHeight: 150, position: 'relative', display: 'flex' }}
          >
            <Scrim />
            <Box
              zIndex={1}
              position="relative"
              flex={1}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                {!hideBanner && (
                  <ProgramOpportunityBanner
                    program={program}
                    opportunities={opportunities}
                    enrollmentCount={enrollmentCount}
                    stat={stat}
                  />
                )}
              </Box>
              <Stack p={2} direction="row" alignItems="center" justifyContent="space-between">
                <Stack>
                  <Typography color="primary.contrastText" variant="h5">
                    {isLoading ? <Skeleton /> : program?.content.fields.title}
                  </Typography>
                  {!hideCurrency && programCurrency && (
                    <Typography color="primary.contrastText" variant="body2">
                      {isLoading ? <Skeleton /> : t(programCurrency as string)}
                    </Typography>
                  )}
                  {!!program?.syndicating && (
                    <Typography fontStyle="italic" color="primary.contrastText" variant="caption">
                      {t('sponsored by', { orgName: orgContent?.fields?.name })}
                    </Typography>
                  )}
                </Stack>

                {!!program && <ProgramIcon size={50} program={program} />}
              </Stack>
            </Box>
          </CardMedia>
          <CardContent sx={{ flex: 1, display: 'flex' }}>
            <Stack spacing={2} flex={1}>
              <Stack flex={1}>
                {!!stat?.committed.fields ? (
                  <CommittedProgramDisplay
                    program={program}
                    isLoading={isLoading}
                    opportunities={opportunities}
                    stat={stat}
                    tokens={tokens}
                  />
                ) : !!availableOpportunities?.length ? (
                  <Stack spacing={2} justifyContent="space-between" flex={1}>
                    <Stack>
                      <Typography color="text.secondary" variant="overline">
                        {t('potentialRewards')}
                      </Typography>
                      {availableOpportunities?.map(opportunity => (
                        <Stack key={opportunity.programId}>
                          <Typography color="text.secondary">
                            {t('fields', {
                              count: opportunity.fields,
                            })}
                          </Typography>
                          {opportunity.rewards?.opportunities &&
                            Object.values(opportunity.rewards.opportunities).map(reward =>
                              !!reward.amount && (!tokens || tokens?.includes(reward.token)) ? (
                                <Typography
                                  key={reward.token}
                                  color="text.secondary"
                                  variant="caption"
                                >
                                  {' '}
                                  {t('availablePossibleEarnings', {
                                    amount: reward.amount,
                                    context: reward.token,
                                  })}
                                </Typography>
                              ) : null
                            )}
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {isLoading ? <Skeleton /> : program?.content.fields.shortDescription}
                  </Typography>
                )}
              </Stack>

              {!!program?.programId && (
                <ProgramLimitMeter programId={program.programId} hideUnderPercent={0.9} />
              )}

              {!!availableOpportunities?.length && (
                <Typography color="text.secondary" variant="caption">
                  {isLoading ? <Skeleton /> : t('possibleEarningsDescription')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </>
      )}
    </>
  )
}
